/**
 * Standalone "Manage Installers" admin page.
 *
 * Split out of StaffDashboard so it can grow independently — filters, bulk
 * actions, sorting, pagination, exports — without adding weight to the
 * dashboard shell. Self-contained: fetches its own installers / pricing
 * tiers / material-cost tiers / certifications, and hosts its own send-
 * message / broadcast / thread modals.
 *
 * Filters at the top:
 *   - Search (name / email / company)
 *   - Tier chips: Trade / Approved / Elite Approved (mirrors `pricing_tiers`)
 *   - Brand chips: ProBound / ProEpoxy / ProDecor (matches `access.<brand>`)
 * Filters compose additively — pick multiple tier or brand chips to widen
 * the result set, deselect all to see everything.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Search, Users, Plus, Eye, MessageSquare, MessageCircle,
  Megaphone, Mail, Phone, Calendar, PoundSterling, X, Download, CheckSquare, Square, Trash2, UserPlus, MapPin,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import ProPortalHeader from '@/components/ProPortalHeader';
import { ApprovedBadgeList } from '@/components/certifications/ApprovedBadge';
import SendMessageModal from '@/components/messaging/SendMessageModal';
import InstallerRecentMessages from '@/components/messaging/InstallerRecentMessages';
import BroadcastPicker from '@/components/messaging/BroadcastPicker';
import MessageThreadModal from '@/components/messaging/MessageThreadModal';
import BulkInviteModal from '@/components/installers/BulkInviteModal';
import InviteInstallerDialog from '@/components/installers/InviteInstallerDialog';
import TidyNamesButton from '@/components/installers/TidyNamesButton';
import { ApprovalAssistDrawer } from '@/components/approvals/ApprovalAssistDrawer';
import { OxApprovedStrip } from '@/components/approvals/OxApprovedStrip';
import { assistApi } from '@/components/approvals/assistApi';
import useInstallerTiers from '@/hooks/useInstallerTiers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BRANDS = [
  { id: 'probound', label: 'ProBound', accent: 'text-[#2F5E96]', chip: 'bg-[#2F5E96]/15 text-[#2F5E96] border-[#2F5E96]/30' },
  { id: 'proepoxy', label: 'ProEpoxy', accent: 'text-[#7A0B7A]', chip: 'bg-[#7A0B7A]/15 text-[#7A0B7A] border-[#7A0B7A]/30' },
  { id: 'prodecor', label: 'ProDecor', accent: 'text-[#D4A24B]', chip: 'bg-[#D4A24B]/15 text-[#8a6a20] border-[#D4A24B]/40' },
];

// Ordered from most-common to least so chips lay out predictably.
const TIER_LABELS_IN_ORDER = ['Trade', 'Approved', 'Elite Approved'];


function authHeaders() {
  return { 'Content-Type': 'application/json' };
}

export default function ManageInstallersPage() {
  const navigate = useNavigate();

  const [installers, setInstallers] = useState([]);
  const [pricingTiers, setPricingTiers] = useState([]);
  const { tiers: tierSlugs, labels: TIER_SLUG_LABELS } = useInstallerTiers();
  const [certsByInstaller, setCertsByInstaller] = useState({});
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState(new Set()); // set of tier labels
  const [brandFilter, setBrandFilter] = useState(new Set()); // set of brand ids

  const [messageTarget, setMessageTarget] = useState(null);
  const [broadcastPickerOpen, setBroadcastPickerOpen] = useState(false);
  const [broadcastConfig, setBroadcastConfig] = useState(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [threadTarget, setThreadTarget] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});

  // Bulk selection — a Set of user_ids of ticked installers. Selection
  // survives filter changes so staff can compose a hand-picked list across
  // multiple filter passes.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkTierMenuOpen, setBulkTierMenuOpen] = useState(false);
  const [bulkAccessMenuOpen, setBulkAccessMenuOpen] = useState(false);
  const [deletedCount, setDeletedCount] = useState(0);
  const [bulkInviteOpen, setBulkInviteOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [approvalSummary, setApprovalSummary] = useState({});
  const [assistTarget, setAssistTarget] = useState(null);

  const loadApprovalSummary = useCallback(() => {
    assistApi.summary().then(setApprovalSummary).catch(() => {});
  }, []);
  useEffect(() => { loadApprovalSummary(); }, [loadApprovalSummary]);
  const onApprovalChanged = useCallback((d) => {
    loadApprovalSummary();
    if (d?.overall_status === 'approved') {
      setInstallers((prev) => prev.map((i) => (i.user_id === d.user_id ? { ...i, ox_approved: { ...(i.ox_approved || {}), [d.brand]: true } } : i)));
    }
  }, [loadApprovalSummary]);

  // ── Data loading ─────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [instRes, ptRes, certsRes] = await Promise.all([
        fetch(`${API_URL}/api/staff/installers`, { credentials: 'include', headers: authHeaders() }),
        fetch(`${API_URL}/api/admin/pricing-tiers`, { credentials: 'include', headers: authHeaders() }),
        fetch(`${API_URL}/api/admin/certifications`, { credentials: 'include', headers: authHeaders() }).catch(() => null),
      ]);

      if (instRes.ok) setInstallers(await instRes.json());
      if (ptRes.ok) setPricingTiers(await ptRes.json());
      if (certsRes && certsRes.ok) {
        const list = await certsRes.json();
        const map = {};
        (list || []).forEach((c) => {
          if (!map[c.user_id]) map[c.user_id] = [];
          map[c.user_id].push(c);
        });
        setCertsByInstaller(map);
      }
    } catch (e) {
      toast.error('Failed to load installers', { description: String(e).slice(0, 200) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Poll the 30-day recovery bin count so the header pill flags when there
  // are installers waiting to be restored. Cheap query — staff table only.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/staff/deleted-installers`, {
          credentials: 'include', headers: authHeaders(),
        });
        if (!r.ok) return;
        const list = await r.json();
        if (alive) setDeletedCount(Array.isArray(list) ? list.length : 0);
      } catch (_e) { /* silent */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // ── Unread badge polling ─────────────────────────────────────────────

  const fetchUnread = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/messages/unread-summary`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (!r.ok) return;
      const d = await r.json();
      setUnreadCounts(d.counts || {});
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, 45000);
    return () => clearInterval(t);
  }, [fetchUnread]);

  const openThread = useCallback((installer) => {
    setThreadTarget({ id: installer.user_id, name: installer.name });
    setUnreadCounts((prev) => {
      if (!prev[installer.user_id]) return prev;
      const { [installer.user_id]: _dropped, ...rest } = prev;
      return rest;
    });
    (async () => {
      try {
        await fetch(`${API_URL}/api/messages/thread/mark-read`, {
          method: 'POST', credentials: 'include', headers: authHeaders(),
          body: JSON.stringify({ recipient_ref: installer.user_id }),
        });
      } catch (_e) { /* swallow */ }
    })();
  }, []);

  // ── Mutations (tier / mct / consent) ─────────────────────────────────

  const setInstallerTier = async (installerId, tierId) => {
    const prev = installers.find((i) => i.user_id === installerId)?.pricing_tier_id;
    setInstallers((list) => list.map((i) => i.user_id === installerId ? { ...i, pricing_tier_id: tierId } : i));
    try {
      const r = await fetch(`${API_URL}/api/admin/users/${installerId}/pricing-tier`, {
        method: 'PUT', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ pricing_tier_id: tierId }),
      });
      if (!r.ok) throw new Error('Failed to set tier');
      const label = pricingTiers.find((t) => t.tier_id === tierId)?.label || 'tier';
      toast.success(`Moved to ${label}`);
    } catch (e) {
      setInstallers((list) => list.map((i) => i.user_id === installerId ? { ...i, pricing_tier_id: prev } : i));
      toast.error(e.message);
    }
  };

  const setInstallerMct = async (installerId, brand, slug) => {
    const installer = installers.find((i) => i.user_id === installerId);
    const prev = installer?.pricing_tier_by_brand || {};
    const next = { ...prev, [brand]: slug || null };
    setInstallers((list) => list.map((i) => i.user_id === installerId ? { ...i, pricing_tier_by_brand: next } : i));
    try {
      const r = await fetch(`${API_URL}/api/staff/users/${installerId}/pricing-tier-by-brand`, {
        method: 'PUT', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify(next),
      });
      if (!r.ok) throw new Error('Failed to set tier');
      const label = TIER_SLUG_LABELS[slug];
      toast.success(label ? `${brand} → ${label}` : `${brand} tier cleared`);
    } catch (e) {
      setInstallers((list) => list.map((i) => i.user_id === installerId ? { ...i, pricing_tier_by_brand: prev } : i));
      toast.error(e.message);
    }
  };

  const toggleConsent = async (installerId, field, next) => {
    setInstallers((list) => list.map((i) => i.user_id === installerId ? { ...i, [field]: next } : i));
    try {
      const r = await fetch(`${API_URL}/api/staff/installers/${installerId}/consent`, {
        method: 'PATCH', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ [field]: next }),
      });
      if (!r.ok) throw new Error(await r.text());
    } catch (_e) {
      setInstallers((list) => list.map((i) => i.user_id === installerId ? { ...i, [field]: !next } : i));
    }
  };

  // ── Filtering ────────────────────────────────────────────────────────

  // Map tier_id → label once so we can filter by human label.
  const tierIdToLabel = useMemo(() => {
    const m = {};
    pricingTiers.forEach((t) => { m[t.tier_id] = t.label; });
    return m;
  }, [pricingTiers]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return installers.filter((i) => {
      if (q) {
        const haystack = `${i.name || ''} ${i.email || ''} ${i.company_name || ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (tierFilter.size > 0) {
        const label = tierIdToLabel[i.pricing_tier_id] || 'Unassigned';
        if (!tierFilter.has(label)) return false;
      }
      if (brandFilter.size > 0) {
        const has = Array.from(brandFilter).some((b) => i.access?.[b]);
        if (!has) return false;
      }
      return true;
    });
  }, [installers, searchTerm, tierFilter, brandFilter, tierIdToLabel]);

  // Live counts on each tier chip so staff see the split at a glance.
  const tierCounts = useMemo(() => {
    const c = {};
    installers.forEach((i) => {
      const l = tierIdToLabel[i.pricing_tier_id] || 'Unassigned';
      c[l] = (c[l] || 0) + 1;
    });
    return c;
  }, [installers, tierIdToLabel]);

  const brandCounts = useMemo(() => {
    const c = { probound: 0, proepoxy: 0, prodecor: 0 };
    installers.forEach((i) => {
      BRANDS.forEach(({ id }) => { if (i.access?.[id]) c[id] += 1; });
    });
    return c;
  }, [installers]);

  const toggleSet = (setter, current, value) => {
    const next = new Set(current);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  };

  const clearFilters = () => {
    setTierFilter(new Set());
    setBrandFilter(new Set());
    setSearchTerm('');
  };

  const anyFilterActive = searchTerm.length > 0 || tierFilter.size > 0 || brandFilter.size > 0;

  // ── Bulk selection ───────────────────────────────────────────────────

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filtered.forEach((i) => next.add(i.user_id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Selected installers hydrated from the current `installers` list. We keep
  // the SET as source of truth so filter changes don't drop unrelated picks.
  const selectedInstallers = useMemo(
    () => installers.filter((i) => selectedIds.has(i.user_id)),
    [installers, selectedIds],
  );

  const bulkBroadcastSms = () => {
    if (selectedInstallers.length === 0) return;
    // Reuse the existing broadcast modal — it already handles installer_ids.
    setBroadcastConfig({
      channel: 'sms',
      label: `${selectedInstallers.length} selected installer${selectedInstallers.length === 1 ? '' : 's'}`,
      installer_ids: selectedInstallers.map((i) => i.user_id),
      brand_access: [], // filter is by explicit ids, not by brand
    });
  };

  const bulkSetTier = async (tierId) => {
    if (selectedInstallers.length === 0) return;
    setBulkTierMenuOpen(false);
    setBulkBusy(true);
    const label = pricingTiers.find((t) => t.tier_id === tierId)?.label || 'tier';
    const results = await Promise.allSettled(
      selectedInstallers.map((i) =>
        fetch(`${API_URL}/api/admin/users/${i.user_id}/pricing-tier`, {
          method: 'PUT', credentials: 'include', headers: authHeaders(),
          body: JSON.stringify({ pricing_tier_id: tierId }),
        }).then((r) => (r.ok ? i.user_id : Promise.reject()))
      )
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - ok;
    // Reflect the successful moves locally so the tier badge / dropdown
    // updates without a full refetch.
    setInstallers((list) => list.map((i) => selectedIds.has(i.user_id) ? { ...i, pricing_tier_id: tierId } : i));
    setBulkBusy(false);
    clearSelection();
    if (failed === 0) toast.success(`${ok} installer${ok === 1 ? '' : 's'} moved to ${label}`);
    else toast.warning(`${ok} moved to ${label} · ${failed} failed`);
  };

  const bulkSetAccess = async (brandId, granted) => {
    if (selectedInstallers.length === 0) return;
    setBulkAccessMenuOpen(false);
    setBulkBusy(true);
    const brandLabel = BRANDS.find((b) => b.id === brandId)?.label || brandId;

    // The access endpoint replaces the full access object, so we merge the
    // new flag onto each installer's CURRENT access rather than blindly
    // overwriting it (which would revoke the other two brands).
    const results = await Promise.allSettled(
      selectedInstallers.map((i) => {
        const nextAccess = { ...(i.access || {}), [brandId]: granted };
        return fetch(`${API_URL}/api/staff/installers/${i.user_id}/access`, {
          method: 'PUT', credentials: 'include', headers: authHeaders(),
          body: JSON.stringify({ access: nextAccess }),
        }).then((r) => (r.ok ? { uid: i.user_id, access: nextAccess } : Promise.reject()));
      })
    );
    const okRows = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
    const failed = results.length - okRows.length;

    // Reflect the successful updates locally so the brand chips + MCT rows
    // on each card update instantly without a full reload.
    const okMap = new Map(okRows.map((r) => [r.uid, r.access]));
    setInstallers((list) => list.map((i) => okMap.has(i.user_id) ? { ...i, access: okMap.get(i.user_id) } : i));

    setBulkBusy(false);
    const verb = granted ? 'granted' : 'revoked';
    if (failed === 0) toast.success(`${brandLabel} access ${verb} for ${okRows.length} installer${okRows.length === 1 ? '' : 's'}`);
    else toast.warning(`${okRows.length} ${verb} · ${failed} failed`);
  };

  const bulkExportCsv = () => {    if (selectedInstallers.length === 0) return;
    // Client-side CSV — no round-trip needed for the columns we care about.
    const rows = [
      ['Name', 'Company', 'Email', 'Phone', 'Tier', 'ProBound', 'ProEpoxy', 'ProDecor', 'SMS opt-in', 'WhatsApp opt-in', 'Joined'],
      ...selectedInstallers.map((i) => [
        i.name || '', i.company_name || '', i.email || '', i.phone || '',
        tierIdToLabel[i.pricing_tier_id] || '',
        i.access?.probound ? 'Y' : '', i.access?.proepoxy ? 'Y' : '', i.access?.prodecor ? 'Y' : '',
        i.sms_opt_in === false ? 'N' : 'Y', i.whatsapp_opt_in === false ? 'N' : 'Y',
        i.created_at ? new Date(i.created_at).toISOString().slice(0, 10) : '',
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => {
      const s = String(c ?? '');
      // Excel-safe: quote every cell that contains a comma, quote or newline;
      // escape internal quotes by doubling.
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().slice(0, 10);
    const a = document.createElement('a');
    a.href = url;
    a.download = `installers-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${selectedInstallers.length} installer${selectedInstallers.length === 1 ? '' : 's'}`);
  };

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50" data-testid="manage-installers-page">
      <ProPortalHeader activeSection="staff" />

      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              onClick={() => navigate('/staff')}
              className="p-2 hover:bg-gray-200 rounded-lg shrink-0"
              data-testid="manage-installers-back"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Users className="w-5 h-5 text-[#7A0B7A] shrink-0" />
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Manage Installers</h1>
                <span className="text-xs font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5" data-testid="mi-count">
                  {filtered.length} / {installers.length}
                </span>
              </div>
              <p className="hidden sm:block text-sm text-gray-500 mt-0.5">
                Search, filter by tier or brand, message and manage every installer in one place.
              </p>
            </div>
          </div>
          {/* Action buttons — wrap on mobile so the title row stays clean. */}
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/staff/installers-deleted')}
              data-testid="mi-recently-deleted-btn"
              className="border-red-300 text-red-700 hover:bg-red-50"
              title="Recently deleted — restore within 30 days"
            >
              <Trash2 className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Recently deleted</span>
              {deletedCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold" data-testid="mi-recently-deleted-count">
                  {deletedCount > 99 ? '99+' : deletedCount}
                </span>
              )}
            </Button>
            <TidyNamesButton onDone={loadAll} />
            <Button
              size="sm"
              onClick={() => setInviteOpen(true)}
              data-testid="mi-add-installer-btn"
              className="bg-[#7A0B7A] hover:bg-[#5e085e] text-white font-bold"
              title="Add an installer and send their invite"
            >
              <UserPlus className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Add installer</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkInviteOpen(true)}
              data-testid="mi-bulk-invite-btn"
              className="border-[#7A0B7A]/40 text-[#7A0B7A] hover:bg-[#7A0B7A]/5"
              title="Bulk invite installers"
            >
              <UserPlus className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Bulk invite</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBroadcastPickerOpen(true)}
              data-testid="mi-broadcast-btn"
              className="border-amber-300 text-amber-800 hover:bg-amber-50"
              title="Broadcast a message"
            >
              <Megaphone className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Broadcast</span>
            </Button>
          </div>
        </div>

        {/* Filters row */}
        <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 mb-5 space-y-3" data-testid="mi-filters">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name, email or company…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="mi-search"
              />
            </div>
            {anyFilterActive && (
              <button
                onClick={clearFilters}
                className="text-xs font-semibold text-gray-500 hover:text-gray-800 inline-flex items-center gap-1 shrink-0"
                data-testid="mi-clear-filters"
              >
                <X className="w-3 h-3" /> Clear filters
              </button>
            )}
          </div>

          <div className="flex flex-col md:flex-row gap-3">
            <FilterChipGroup
              testIdPrefix="mi-tier"
              label="Tier"
              options={TIER_LABELS_IN_ORDER.map((l) => ({
                id: l, label: l, count: tierCounts[l] || 0,
                colorOn: 'bg-emerald-600 text-white border-emerald-600',
                colorOff: 'bg-white text-emerald-800 border-emerald-200 hover:border-emerald-400',
              }))}
              active={tierFilter}
              onToggle={(v) => toggleSet(setTierFilter, tierFilter, v)}
            />
            <FilterChipGroup
              testIdPrefix="mi-brand"
              label="Brand access"
              options={BRANDS.map((b) => ({
                id: b.id, label: b.label, count: brandCounts[b.id] || 0,
                colorOn: b.id === 'probound' ? 'bg-[#2F5E96] text-white border-[#2F5E96]'
                       : b.id === 'proepoxy' ? 'bg-[#7A0B7A] text-white border-[#7A0B7A]'
                       : 'bg-[#D4A24B] text-white border-[#D4A24B]',
                colorOff: b.id === 'probound' ? 'bg-white text-[#2F5E96] border-[#2F5E96]/30 hover:border-[#2F5E96]/60'
                       : b.id === 'proepoxy' ? 'bg-white text-[#7A0B7A] border-[#7A0B7A]/30 hover:border-[#7A0B7A]/60'
                       : 'bg-white text-[#8a6a20] border-[#D4A24B]/40 hover:border-[#D4A24B]/70',
              }))}
              active={brandFilter}
              onToggle={(v) => toggleSet(setBrandFilter, brandFilter, v)}
            />
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[#7A0B7A] border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading installers…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-200" data-testid="mi-empty">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {anyFilterActive ? 'No installers match these filters.' : 'No installers yet.'}
            </p>
            {anyFilterActive && (
              <button onClick={clearFilters} className="mt-3 text-sm font-semibold text-[#7A0B7A] hover:underline">
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((installer) => (
              <InstallerCard
                key={installer.user_id}
                installer={installer}
                tierLabel={tierIdToLabel[installer.pricing_tier_id]}
                certifications={certsByInstaller[installer.user_id] || []}
                pricingTiers={pricingTiers}
                tierSlugs={tierSlugs}
                unreadCount={unreadCounts[installer.user_id] || 0}
                historyRefreshKey={historyRefreshKey}
                isSelected={selectedIds.has(installer.user_id)}
                onToggleSelect={() => toggleOne(installer.user_id)}
                onSetTier={(tierId) => setInstallerTier(installer.user_id, tierId)}
                onSetMct={(brand, tierId) => setInstallerMct(installer.user_id, brand, tierId)}
                onToggleConsent={(field, next) => toggleConsent(installer.user_id, field, next)}
                onSendSms={() => setMessageTarget({ name: installer.name, phone: installer.phone, ref: installer.user_id, channel: 'sms' })}
                onSendWhatsapp={() => setMessageTarget({ name: installer.name, phone: installer.phone, ref: installer.user_id, channel: 'whatsapp' })}
                onOpenThread={() => openThread(installer)}
                onAddLead={() => navigate(`/leads?installer=${installer.user_id}&action=add`)}
                onView={() => navigate(`/staff/installers/${installer.user_id}`)}
                approvalSummary={approvalSummary[installer.user_id] || {}}
                onAssist={(brand) => setAssistTarget({ installer, brand })}
                onApproved={onApprovalChanged}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <ApprovalAssistDrawer
        open={Boolean(assistTarget)}
        onClose={() => setAssistTarget(null)}
        userId={assistTarget?.installer?.user_id}
        brand={assistTarget?.brand || 'probound'}
        installer={assistTarget?.installer}
        onChanged={onApprovalChanged}
      />
      <SendMessageModal
        open={Boolean(messageTarget)}
        onClose={() => setMessageTarget(null)}
        channel={messageTarget?.channel || 'sms'}
        recipientName={messageTarget?.name}
        recipientPhone={messageTarget?.phone}
        recipientRef={messageTarget?.ref}
        onSent={() => setHistoryRefreshKey((v) => v + 1)}
      />
      <BroadcastPicker
        open={broadcastPickerOpen}
        onClose={() => setBroadcastPickerOpen(false)}
        installers={installers}
        onPick={(cfg) => { setBroadcastPickerOpen(false); setBroadcastConfig(cfg); }}
      />
      <SendMessageModal
        open={Boolean(broadcastConfig)}
        onClose={() => setBroadcastConfig(null)}
        channel={broadcastConfig?.channel || 'sms'}
        broadcast={broadcastConfig ? {
          label: broadcastConfig.label,
          brand_access: broadcastConfig.brand_access || [],
          installer_ids: broadcastConfig.installer_ids || undefined,
        } : null}
        onSent={() => { setHistoryRefreshKey((v) => v + 1); setBroadcastConfig(null); }}
      />
      <MessageThreadModal
        open={Boolean(threadTarget)}
        onClose={() => setThreadTarget(null)}
        installerId={threadTarget?.id}
        installerName={threadTarget?.name}
      />
      <BulkInviteModal
        open={bulkInviteOpen}
        onClose={() => setBulkInviteOpen(false)}
        pricingTiers={pricingTiers}
        onCompleted={() => { loadAll(); }}
      />
      <InviteInstallerDialog open={inviteOpen} onClose={() => setInviteOpen(false)} onInvited={loadAll} />

      {/* ── Floating bulk-actions bar ─────────────────────────────────
          Slides up from the bottom whenever ≥1 installer is ticked.
          Actions operate on `selectedInstallers` (source-of-truth Set),
          which persists across filter changes so staff can build a
          hand-picked list across multiple filter passes. */}
      {selectedIds.size > 0 && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-[#27292E] text-white rounded-2xl shadow-2xl px-4 py-2.5 flex items-center gap-2 sm:gap-3 flex-wrap max-w-[calc(100%-2rem)]"
          data-testid="mi-bulk-bar"
        >
          <span className="font-semibold text-sm shrink-0" data-testid="mi-bulk-count">
            {selectedIds.size} selected
          </span>
          <span className="text-white/30">·</span>
          <button
            onClick={selectAllFiltered}
            className="text-xs font-semibold text-white/80 hover:text-white"
            data-testid="mi-bulk-select-all"
          >
            Select all visible ({filtered.length})
          </button>
          <button
            onClick={clearSelection}
            className="text-xs font-semibold text-white/60 hover:text-white"
            data-testid="mi-bulk-clear"
          >
            Clear
          </button>
          <span className="hidden sm:inline text-white/30">·</span>
          <button
            onClick={bulkBroadcastSms}
            disabled={bulkBusy}
            className="inline-flex items-center gap-1 text-xs font-bold bg-blue-500 hover:bg-blue-400 disabled:bg-gray-500 rounded-lg px-2.5 py-1.5"
            data-testid="mi-bulk-sms"
          >
            <MessageSquare className="w-3.5 h-3.5" /> Send SMS
          </button>
          {pricingTiers.length > 0 && (
            <div className="relative">
              <button
                onClick={() => { setBulkTierMenuOpen((v) => !v); setBulkAccessMenuOpen(false); }}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1 text-xs font-bold bg-emerald-500 hover:bg-emerald-400 disabled:bg-gray-500 rounded-lg px-2.5 py-1.5"
                data-testid="mi-bulk-tier-btn"
              >
                <PoundSterling className="w-3.5 h-3.5" /> Set tier
              </button>
              {bulkTierMenuOpen && (
                <div
                  className="absolute bottom-full mb-2 right-0 bg-white text-gray-900 rounded-xl shadow-2xl border border-gray-200 min-w-[180px] overflow-hidden"
                  data-testid="mi-bulk-tier-menu"
                >
                  {pricingTiers.map((t) => (
                    <button
                      key={t.tier_id}
                      onClick={() => bulkSetTier(t.tier_id)}
                      className="block w-full text-left text-xs font-semibold px-3 py-2 hover:bg-emerald-50 hover:text-emerald-800"
                      data-testid={`mi-bulk-tier-${t.tier_id}`}
                    >
                      {t.label}{t.is_default ? ' (default)' : ''}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Brand-access bulk grant / revoke — one click onboards a whole
              batch of new installers onto ProBound / ProEpoxy / ProDecor. */}
          <div className="relative">
            <button
              onClick={() => { setBulkAccessMenuOpen((v) => !v); setBulkTierMenuOpen(false); }}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1 text-xs font-bold bg-purple-500 hover:bg-purple-400 disabled:bg-gray-500 rounded-lg px-2.5 py-1.5"
              data-testid="mi-bulk-access-btn"
            >
              <CheckSquare className="w-3.5 h-3.5" /> Set access
            </button>
            {bulkAccessMenuOpen && (
              <div
                className="absolute bottom-full mb-2 right-0 bg-white text-gray-900 rounded-xl shadow-2xl border border-gray-200 min-w-[220px] overflow-hidden"
                data-testid="mi-bulk-access-menu"
              >
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border-b border-emerald-100">
                  Grant access
                </div>
                {BRANDS.map((b) => (
                  <button
                    key={`grant-${b.id}`}
                    onClick={() => bulkSetAccess(b.id, true)}
                    className="flex items-center gap-2 w-full text-left text-xs font-semibold px-3 py-2 hover:bg-emerald-50"
                    data-testid={`mi-bulk-grant-${b.id}`}
                  >
                    <span className={`inline-block w-2 h-2 rounded-full`} style={{ backgroundColor: b.id === 'probound' ? '#2F5E96' : b.id === 'proepoxy' ? '#7A0B7A' : '#D4A24B' }} />
                    Grant {b.label}
                  </button>
                ))}
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-red-700 bg-red-50 border-y border-red-100">
                  Revoke access
                </div>
                {BRANDS.map((b) => (
                  <button
                    key={`revoke-${b.id}`}
                    onClick={() => bulkSetAccess(b.id, false)}
                    className="flex items-center gap-2 w-full text-left text-xs font-semibold px-3 py-2 hover:bg-red-50 text-gray-700"
                    data-testid={`mi-bulk-revoke-${b.id}`}
                  >
                    <span className={`inline-block w-2 h-2 rounded-full opacity-40`} style={{ backgroundColor: b.id === 'probound' ? '#2F5E96' : b.id === 'proepoxy' ? '#7A0B7A' : '#D4A24B' }} />
                    Revoke {b.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={bulkExportCsv}
            disabled={bulkBusy}
            className="inline-flex items-center gap-1 text-xs font-bold bg-gray-600 hover:bg-gray-500 disabled:bg-gray-500 rounded-lg px-2.5 py-1.5"
            data-testid="mi-bulk-export"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Chip filter group
// ─────────────────────────────────────────────────────────────────────────

function FilterChipGroup({ label, options, active, onToggle, testIdPrefix }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider shrink-0">{label}:</span>
      {options.map((opt) => {
        const isOn = active.has(opt.id);
        return (
          <button
            key={opt.id}
            onClick={() => onToggle(opt.id)}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold border rounded-full px-3 py-1 transition ${isOn ? opt.colorOn : opt.colorOff}`}
            data-testid={`${testIdPrefix}-${opt.id.toLowerCase().replace(/\s+/g, '-')}`}
            aria-pressed={isOn}
          >
            {opt.label}
            <span className={`text-[10px] font-mono ${isOn ? 'opacity-90' : 'opacity-60'}`}>{opt.count}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Individual installer card
// ─────────────────────────────────────────────────────────────────────────

function InstallerCard({
  installer, tierLabel, certifications, pricingTiers, tierSlugs, unreadCount,
  historyRefreshKey, isSelected, onToggleSelect,
  onSetTier, onSetMct, onToggleConsent, onSendSms, onSendWhatsapp,
  onOpenThread, onAddLead, onView, approvalSummary, onAssist, onApproved,
}) {
  return (
    <Card
      className={`hover:shadow-lg transition-shadow relative ${isSelected ? 'ring-2 ring-[#7A0B7A] border-[#7A0B7A]' : ''}`}
      data-testid={`installer-card-${installer.user_id}`}
    >
      {/* Bulk-selection checkbox — floats top-left so it never fights with
          the tier badge on the top-right. */}
      <label
        className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-white/90 rounded-md px-1.5 py-0.5 cursor-pointer border border-gray-200 hover:border-[#7A0B7A] shadow-sm"
        onClick={(e) => e.stopPropagation()}
        data-testid={`installer-select-label-${installer.user_id}`}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="w-3.5 h-3.5 accent-[#7A0B7A] cursor-pointer"
          data-testid={`installer-select-${installer.user_id}`}
        />
      </label>
      <CardContent className="p-4 pt-8">
        <div className="flex items-start justify-between mb-3">
          <div className="min-w-0">
            <h3 className="font-bold text-[#27292E] truncate">{installer.name}</h3>
            {installer.company_name && (
              <p className="text-sm text-gray-500 truncate">{installer.company_name}</p>
            )}
            <div className="mt-1.5">
              <ApprovedBadgeList certs={certifications} />
            </div>
          </div>
          {tierLabel && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 shrink-0" data-testid={`installer-tier-badge-${installer.user_id}`}>
              {tierLabel}
            </span>
          )}
        </div>

        <div className="space-y-1.5 text-sm text-gray-600 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <Mail className="w-4 h-4 shrink-0" />
            <span className="truncate">{installer.email}</span>
          </div>
          {installer.phone && (
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 shrink-0" />
              <span>{installer.phone}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 shrink-0" />
            <span>Joined {new Date(installer.created_at).toLocaleDateString()}</span>
          </div>
        </div>

        <CoverageStrip installer={installer} />

        <div className="flex gap-4 mb-3 p-3 bg-gray-50 rounded-lg">
          <div className="text-center flex-1">
            <p className="text-2xl font-bold text-[#7A0B7A]">{installer.lead_count || 0}</p>
            <p className="text-xs text-gray-500">Leads</p>
          </div>
          <div className="text-center flex-1">
            <p className="text-2xl font-bold text-[#2F5E96]">{installer.quote_count || 0}</p>
            <p className="text-xs text-gray-500">Quotes</p>
          </div>
        </div>

        <div className="flex gap-1 flex-wrap mb-3">
          {BRANDS.map(({ id, label, chip }) => (
            <span
              key={id}
              className={`px-2 py-0.5 rounded text-xs font-medium ${installer.access?.[id] ? chip : 'bg-gray-100 text-gray-400 line-through'}`}
            >
              {label}
            </span>
          ))}
        </div>

        {pricingTiers.length > 0 && (
          <div className="mb-2 flex items-center gap-2">
            <PoundSterling className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
            <span className="text-xs text-gray-500 shrink-0">Tier:</span>
            <select
              value={installer.pricing_tier_id || ''}
              onChange={(e) => onSetTier(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 text-xs border border-gray-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:border-emerald-500"
              data-testid={`installer-tier-${installer.user_id}`}
            >
              {!installer.pricing_tier_id && <option value="">— unassigned —</option>}
              {pricingTiers.map((t) => (
                <option key={t.tier_id} value={t.tier_id}>{t.label}{t.is_default ? ' (default)' : ''}</option>
              ))}
            </select>
          </div>
        )}

        {BRANDS.some((b) => installer.access?.[b.id]) && (
          <div className="mb-3 mt-1 space-y-1 border-t border-gray-100 pt-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Tier per brand</div>
            {BRANDS.map(({ id, accent }) => {
              if (!installer.access?.[id]) return null;
              const current = installer.pricing_tier_by_brand?.[id] || '';
              return (
                <div key={id} className="flex items-center gap-2">
                  <span className={`text-[11px] font-bold capitalize shrink-0 w-16 ${accent}`}>{id}</span>
                  <select
                    value={current}
                    onChange={(e) => onSetMct(id, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 text-xs border border-gray-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:border-emerald-500"
                    data-testid={`installer-mct-${id}-${installer.user_id}`}
                  >
                    <option value="">— unassigned (Trade) —</option>
                    {tierSlugs.map((t) => (
                      <option key={t.slug} value={t.slug}>{t.label}</option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        )}

        <OxApprovedStrip installer={installer} summary={approvalSummary} onAssist={onAssist} onApproved={onApproved} />

        <div className="space-y-2">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={onView}>
              <Eye className="w-4 h-4 mr-1" /> View
            </Button>
            <Button size="sm" className="flex-1 bg-[#7A0B7A] hover:bg-[#5a085a]" onClick={onAddLead}>
              <Plus className="w-4 h-4 mr-1" /> Add Lead
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm"
              className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-40"
              disabled={!installer.phone || installer.sms_opt_in === false}
              onClick={onSendSms}
              data-testid={`installer-sms-${installer.user_id}`}
              title={!installer.phone ? 'No phone number' : installer.sms_opt_in === false ? 'Opted out of SMS' : 'Send an SMS'}
            >
              <MessageSquare className="w-4 h-4 mr-1" /> SMS Text
            </Button>
            <Button
              variant="outline" size="sm"
              className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
              disabled={!installer.phone || installer.whatsapp_opt_in === false}
              onClick={onSendWhatsapp}
              data-testid={`installer-whatsapp-${installer.user_id}`}
              title={!installer.phone ? 'No phone number' : installer.whatsapp_opt_in === false ? 'Opted out of WhatsApp' : 'Send a WhatsApp'}
            >
              <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
            </Button>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-gray-500 pt-1">
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={installer.sms_opt_in !== false}
                onChange={(e) => onToggleConsent('sms_opt_in', e.target.checked)}
                className="w-3 h-3 accent-blue-600"
                data-testid={`installer-sms-optin-${installer.user_id}`}
              />
              SMS OK
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={installer.whatsapp_opt_in !== false}
                onChange={(e) => onToggleConsent('whatsapp_opt_in', e.target.checked)}
                className="w-3 h-3 accent-emerald-600"
                data-testid={`installer-wa-optin-${installer.user_id}`}
              />
              WhatsApp OK
            </label>
          </div>
        </div>

        <InstallerRecentMessages
          installerId={installer.user_id}
          refreshKey={historyRefreshKey}
          onViewThread={onOpenThread}
          unreadCount={unreadCount}
        />
      </CardContent>
    </Card>
  );
}


/**
 * Compact regions strip for the installer list card. Shows up to 3
 * region chips + "+N more" fallback. Silent when the installer hasn't
 * set any coverage yet so the card doesn't grow a blank row.
 */
function CoverageStrip({ installer }) {
  const regions = Array.isArray(installer.lead_regions_covered)
    ? installer.lead_regions_covered
    : [];
  const territories = Array.isArray(installer.lead_territories)
    ? installer.lead_territories
    : [];
  if (regions.length === 0 && territories.length === 0) return null;

  const visible = regions.slice(0, 3);
  const overflow = regions.length - visible.length;
  return (
    <div
      className="mb-3 flex items-center gap-1.5 flex-wrap"
      data-testid={`installer-coverage-${installer.user_id}`}
    >
      <MapPin className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
      <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-800">
        Coverage:
      </span>
      {visible.map((r) => (
        <span
          key={r}
          className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-900 border border-emerald-200"
        >
          {r}
        </span>
      ))}
      {overflow > 0 && (
        <span className="text-[10px] font-semibold text-emerald-800">+{overflow} more</span>
      )}
      <span className="text-[10px] text-gray-500 ml-auto">
        {territories.length} territor{territories.length === 1 ? 'y' : 'ies'}
      </span>
    </div>
  );
}
